import { Subject, distinctUntilChanged, map, takeUntil, debounceTime, Observable } from 'rxjs';
import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ContactModel } from 'src/app/models/contact.model';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

const alphaNumericRegex = /^[A-Za-z0-9]{1}/;

enum ScrollDirection {
  UP,
  DOWN,
}

@Component({
  selector: 'tx-dropdown-with-filter',
  templateUrl: './dropdown-with-filter.component.html',
  styleUrls: ['./dropdown-with-filter.component.scss'],
})
export class DropdownWithFilterComponent implements OnInit, OnDestroy {
  @Input() items$!: Observable<any[]>;
  @Input() idField!: string;
  @Input() titleField!: string;
  @Input() matchFields!: string[];
  @Input() menuInstance!: NgbDropdown;
  @Input() showFilter = true;
  @Output() onItemClicked: EventEmitter<any> = new EventEmitter();

  @ViewChild('filterInput') filterInput!: ElementRef;
  @ViewChild('itemsList') itemsList!: ElementRef;

  originalItems: any[] = [];
  filteredItems: any[] = [];
  isLoading: boolean = false;
  searchTerm$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  filterText = '';

  constructor() {}

  ngOnInit() {
    this.isLoading = true;
    this.items$
      .pipe(takeUntil(this.destroy$)) // Unsubscribe when the component is destroyed
      .subscribe((items) => {
        if (items?.length > 0) {
          this.originalItems = items;
          this.filteredItems = items;
        }
        this.isLoading = false;
      });

    this.initFiltering();

    if (this.showFilter) {
      this.focusFilterOnOpening();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  focusFilterOnOpening() {
    setTimeout(() => {
      this.menuInstance.openChange
        .pipe(takeUntil(this.destroy$)) // Unsubscribe when the component is destroyed
        .subscribe((isOpened) => {
          if (isOpened) {
            this.focusFilterInput();
          }
        });
    }, 0);
  }

  initFiltering() {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroy$), // Unsubscribe when the component is destroyed
        debounceTime(250), // wait 250ms after each keystroke before considering the term
        map((term) => term.trim()), // Remove whitespace from begin or end
        distinctUntilChanged() // ignore new term if same as previous term
      )
      .subscribe({
        next: (searchTerm) => {
          this.filteredItems = searchTerm.length > 1 ? this.filterItems(searchTerm) : this.originalItems;
        },
      });
  }

  filterItems(searchTerm: string): ContactModel[] {
    const needle = searchTerm.toLowerCase(); // Convert the search term to lowercase for case-insensitive matching

    return this.originalItems.filter((item) => {
      const haystack = this.matchFields.reduce((result, field) => {
        result += item[field].toLowerCase();
        return result;
      }, '');

      return haystack.indexOf(needle) > -1;
    });
  }

  clearFilter() {
    this.filteredItems = this.originalItems;
    this.filterText = '';
    this.focusFilterInput();
  }

  focusFilterInput() {
    if (this.filterInput) {
      setTimeout(() => {
        this.filterInput?.nativeElement.focus();
      }, 0);
    }
  }

  showAllClients() {
    this.clearFilter();
    this.focusFilterInput();
    return false;
  }

  onSelectItem(item: any) {
    this.onItemClicked.emit(item);
    return false;
  }

  onFilterChange() {
    this.searchTerm$.next(this.filterText);
  }

  onKeyDownFilterInput(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
      case 'Tab':
        const firstItemId = this.filteredItems[0][this.idField];
        this.applyFocusOnElement(`item-${firstItemId}`);
        return false;
    }

    return true;
  }

  onKeyDownItem(event: KeyboardEvent, item: any, index: number) {
    const key = event.shiftKey ? `Shift${event.key}` : event.key;

    switch (key) {
      case 'Enter':
        this.onSelectItem(item);
        break;
      case 'Tab':
      case 'ArrowDown':
        const id = `item-${this.filteredItems[index + 1][this.idField]}`;
        this.applyFocusOnElement(id);
        break;
      case 'ArrowUp':
      case 'ShiftTab':
        if (index > 0) {
          const id = `item-${this.filteredItems[index - 1][this.idField]}`;
          this.applyFocusOnElement(id);
        } else {
          this.focusFilterInput();
        }
        break;
      case 'PageUp':
        this.handlePageUpOrDown(ScrollDirection.UP);
        break;
      case 'PageDown':
        this.handlePageUpOrDown(ScrollDirection.DOWN);
        break;
      default:
        this.onEnterAlphaNumericChar(key);
    }
    return false;
  }

  applyFocusOnElement(elementId: string) {
    document.getElementById(elementId)?.focus();
  }

  onEnterAlphaNumericChar(key: string) {
    // append char to filter input if it is alphanumeric
    if (key.length === 1 && alphaNumericRegex.test(key)) {
      this.filterText += key;
      this.onFilterChange();
      this.focusFilterInput();
    }
  }

  handlePageUpOrDown(direction: ScrollDirection) {
    const itemsList: HTMLElement = this.itemsList.nativeElement;
    // get dropdown height with a small offset to actually select the next item just outside the container
    const dropdownHeight = itemsList.clientHeight - 20;
    const scrollAmount = direction === ScrollDirection.UP ? -dropdownHeight : dropdownHeight;

    // scroll up or down
    itemsList.scrollBy({
      top: scrollAmount,
      behavior: 'smooth',
    });

    // find the first fully visible element
    const visibleItem = this.findFirstFullyVisibleElement(itemsList);

    // focus the element
    if (visibleItem) {
      this.applyFocusOnElement(`item-${visibleItem[this.idField]}`);
    }
  }

  findFirstFullyVisibleElement(itemsList: HTMLElement): any {
    const scrollTop = itemsList.scrollTop;
    const visibleHeight = itemsList.clientHeight;

    return this.filteredItems.find((item) => {
      const element = document.getElementById(`item-${item[this.idField]}`);
      if (!element) return false;

      const itemTop = element.offsetTop - itemsList.offsetTop;
      const itemBottom = itemTop + element.clientHeight;
      return itemTop >= scrollTop && itemBottom <= scrollTop + visibleHeight;
    });
  }
}
